import React, { useState, useEffect } from 'react';
import { feedbackAPI, wardAPI, projectAPI } from '../services/api';
import { Ward, Feedback } from '../types';
import { useNavigate } from 'react-router-dom';

const FeedbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [wards, setWards] = useState<Ward[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [publicFeedback, setPublicFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    county: 'Turkana',
    ward: '',
    feedback_type: 'other' as 'positive' | 'negative' | 'stalled_project' | 'corruption' | 'budget_concern' | 'other',
    message: '',
    project_reference: '',
    is_anonymous: false,
    attachments: [] as File[]
  });

  const getFileIconFromType = (fileType: string) => {
    switch (fileType) {
      case 'image': return '📷';
      case 'audio': return '🎵';
      case 'video': return '🎥';
      case 'document': return '📄';
      default: return '📁';
    }
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return '📷';
    if (file.type.startsWith('audio/')) return '🎵';
    if (file.type.startsWith('video/')) return '🎥';
    if (file.type === 'application/pdf') return '📄';
    if (file.type.includes('word') || file.name.toLowerCase().endsWith('.doc') || file.name.toLowerCase().endsWith('.docx')) return '📄';
    return '📁';
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
        'audio/mp3', 'audio/wav', 'audio/m4a',
        'video/mp4', 'video/mov',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const isValidType = allowedTypes.includes(file.type) || file.name.toLowerCase().endsWith('.doc') || file.name.toLowerCase().endsWith('.docx');
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      return isValidType && isValidSize;
    });
    
    setFormData({...formData, attachments: [...formData.attachments, ...validFiles]});
  };

  const removeAttachment = (index: number) => {
    const newAttachments = formData.attachments.filter((_, i) => i !== index);
    setFormData({...formData, attachments: newAttachments});
  };

  useEffect(() => {
    // Check if user is logged in
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user) {
      setCurrentUser(user);
      // Pre-fill form with user data if logged in and not anonymous
      if (!formData.is_anonymous) {
        setFormData(prev => ({
          ...prev,
          name: user.name,
          email: user.email
        }));
      }
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [wardsRes, feedbackRes, projectsRes] = await Promise.all([
        wardAPI.getByCounty('Turkana'),
        feedbackAPI.getPublic('Turkana'),
        projectAPI.getPublic('Turkana')
      ]);
      
      setWards(wardsRes.data);
      setPublicFeedback(feedbackRes.data);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.message) {
      setMessage('Please fill in all required fields');
      return;
    }

    try {
      setSubmitLoading(true);
      
      let submitData: FormData | any;
      let headers: any = {};
      
      if (formData.attachments.length > 0) {
        // Create FormData for file uploads
        submitData = new FormData();
        
        // Add form fields
        submitData.append('name', formData.name);
        if (formData.email) submitData.append('email', formData.email);
        submitData.append('county', formData.county);
        if (formData.ward) submitData.append('ward', formData.ward);
        submitData.append('feedback_type', formData.feedback_type);
        submitData.append('message', formData.message);
        if (formData.project_reference) submitData.append('project_reference', formData.project_reference);
        submitData.append('is_anonymous', formData.is_anonymous.toString());
        
        // Add files
        formData.attachments.forEach(file => {
          submitData.append('attachments', file);
        });
        
        // Don't set Content-Type header, let browser set it with boundary
      } else {
        // Send as JSON when no attachments
        submitData = {
          name: formData.name,
          email: formData.email || '',
          county: formData.county,
          ward: formData.ward,
          feedback_type: formData.feedback_type,
          message: formData.message,
          project_reference: formData.project_reference,
          is_anonymous: formData.is_anonymous
        };
        headers['Content-Type'] = 'application/json';
      }
      
      await feedbackAPI.submit(submitData, headers);
      
      setMessage('Thank you! Your feedback has been submitted successfully.');
      setFormData({
        name: currentUser?.name || '',
        email: currentUser?.email || '',
        county: 'Turkana',
        ward: '',
        feedback_type: 'other' as any,
        message: '',
        project_reference: '',
        is_anonymous: false,
        attachments: []
      });
      
      loadData();
    } catch (error) {
      setMessage('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const getFeedbackTypeIcon = (type: string) => {
    switch (type) {
      case 'positive': return '👍';
      case 'negative': return '👎';
      case 'stalled_project': return '⏸️';
      case 'corruption': return '🚨';
      case 'budget_concern': return '💰';
      default: return '💬';
    }
  };

  const getFeedbackTypeColor = (type: string) => {
    switch (type) {
      case 'positive': return '#4CAF50';
      case 'negative': return '#F44336';
      case 'stalled_project': return '#FF9800';
      case 'corruption': return '#E91E63';
      case 'budget_concern': return '#2196F3';
      default: return '#757575';
    }
  };

  return (
    <div className="feedback-page">
      <header className="page-header">
        <div className="icon icon-primary">💬</div>
        <h1>Citizen Feedback</h1>
        <p>Share your thoughts on county projects and services. {currentUser ? `Logged in as ${currentUser.name}` : 'No login required.'}</p>
        {currentUser && (
          <div className="user-status">
            <span className="status-badge">{currentUser.role.replace('_', ' ').toUpperCase()}</span>
            {(currentUser.role === 'system_admin' || currentUser.role === 'manager') && (
              <button 
                onClick={() => navigate('/admin')} 
                className="admin-link-btn"
              >
                View Admin Panel
              </button>
            )}
          </div>
        )}
      </header>

      <div className="feedback-content">
        <section className="feedback-form">
          <h2>Submit Feedback</h2>
          
          {message && (
            <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="feedback-form-inner">
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  disabled={formData.is_anonymous || currentUser}
                  placeholder={currentUser ? `Using logged-in name: ${currentUser.name}` : "Enter your name"}
                />
              </div>
              
              <div className="form-group">
                <label>Email (Optional)</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  disabled={formData.is_anonymous || currentUser}
                  placeholder={currentUser ? `Using logged-in email: ${currentUser.email}` : "Enter your email (optional)"}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Ward</label>
                <select
                  value={formData.ward}
                  onChange={(e) => setFormData({...formData, ward: e.target.value})}
                >
                  <option value="">Select Ward (Optional)</option>
                  {wards.map(ward => (
                    <option key={ward.id} value={ward.name}>{ward.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Feedback Type</label>
                <select
                  value={formData.feedback_type}
                  onChange={(e) => setFormData({...formData, feedback_type: e.target.value as any})}
                >
                  <option value="positive">Positive Feedback</option>
                  <option value="negative">Negative Feedback</option>
                  <option value="stalled_project">Stalled Project</option>
                  <option value="corruption">Corruption Report</option>
                  <option value="budget_concern">Budget Concern</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Project Reference (Optional)</label>
              <select
                value={formData.project_reference}
                onChange={(e) => setFormData({...formData, project_reference: e.target.value})}
              >
                <option value="">-- Select a project (optional) --</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.ward_id})</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Message *</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                required
                rows={5}
                placeholder="Please provide detailed feedback..."
              />
            </div>

            <div className="form-group">
              <label>Attachments</label>
              <input
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/gif,audio/mp3,audio/wav,audio/m4a,video/mp4,video/mov,application/pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="file-input"
              />
              <small>Supported formats: Images (JPEG, PNG, GIF), Audio (MP3, WAV, M4A), Video (MP4, MOV), Documents (PDF, DOC, DOCX) - Max 10MB each</small>
              
              {formData.attachments.length > 0 && (
                <div className="attachments-preview">
                  {formData.attachments.map((file, index) => (
                    <div key={index} className="attachment-item">
                      <span>{getFileIcon(file)} {file.name}</span>
                      <button type="button" onClick={() => removeAttachment(index)}>❌</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_anonymous}
                  onChange={(e) => {
                    const isAnonymous = e.target.checked;
                    setFormData({
                      ...formData, 
                      is_anonymous: isAnonymous,
                      name: isAnonymous ? '' : (currentUser?.name || ''),
                      email: isAnonymous ? '' : (currentUser?.email || '')
                    });
                  }}
                  disabled={currentUser !== null}
                />
                Submit anonymously {currentUser ? '(Not available when logged in)' : ''}
              </label>
            </div>

            <button type="submit" disabled={submitLoading} className="submit-btn">
              {submitLoading ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </form>
        </section>

        <section className="feedback-list">
          <h2>Community Feedback</h2>
          
          {loading ? (
            <div className="loading">Loading feedback...</div>
          ) : (
            <div className="feedback-items">
              {publicFeedback.length === 0 ? (
                <p>No public feedback available yet.</p>
              ) : (
                publicFeedback.map(feedback => (
                  <div key={feedback.id} className="feedback-item">
                    <div className="feedback-header">
                      <div className="feedback-type">
                        <span 
                          className="type-icon"
                          style={{ color: getFeedbackTypeColor(feedback.feedback_type) }}
                        >
                          {getFeedbackTypeIcon(feedback.feedback_type)}
                        </span>
                        <span className="type-text">
                          {feedback.feedback_type.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="feedback-meta">
                        <span>{feedback.is_anonymous ? 'Anonymous' : feedback.name}</span>
                        <span>{new Date(feedback.created_at).toLocaleDateString()}</span>
                        {currentUser && feedback.email === currentUser.email && (
                          <button className="edit-btn" onClick={() => setMessage('Edit functionality coming soon')}>✏️</button>
                        )}
                      </div>
                    </div>
                    
                    {feedback.ward && (
                      <div className="feedback-location">
                        📍 {feedback.ward} Ward
                      </div>
                    )}
                    
                    {feedback.project_reference && (
                      <div className="feedback-project">
                        🏗️ Project: {projects.find(p => p.id === feedback.project_reference)?.name || feedback.project_reference}
                      </div>
                    )}
                    
                    <div className="feedback-message">
                      {feedback.message}
                    </div>
                    
                    {feedback.attachments && feedback.attachments.length > 0 && (
                      <div className="feedback-attachments">
                        <strong>Attachments:</strong>
                        {feedback.attachments.map((attachment: any, index: number) => (
                          <a key={index} href={attachment.url} target="_blank" rel="noopener noreferrer">
                            {getFileIconFromType(attachment.file_type)} {attachment.filename}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default FeedbackPage;